/**
 * Dismissible popovers, once, for everything that hangs off a button.
 *
 * Written because the colour picker shipped unable to close, and because the
 * next four things on the plan — brush options, fill options, the layer ⋯ menu,
 * the shortcuts sheet — would each have reimplemented this and got a different
 * subset of it wrong.
 *
 * **The bug that caused this to exist, since it is not obvious and will recur.**
 * Visibility was toggled with the `hidden` attribute, which works through the UA
 * rule `[hidden] { display: none }`. That is a bare attribute selector, so any
 * class rule of the form `.cp { display: grid }` outranks it. The attribute
 * flipped, `aria-expanded` flipped, every piece of state said "closed", and the
 * panel stayed on screen. Nothing was wrong except the specificity.
 *
 * So display here is driven by `[data-open]` and never by `hidden`: the styles
 * and the state cannot disagree, because the same attribute does both jobs.
 * See the `.popover` rules in tools/build_site.mjs.
 *
 * **The side effects this avoids**, each of which is a real popover bug:
 *
 *   - *Closing on the click that opened it.* The anchor's own `pointerdown` is
 *     ignored, so the subsequent `click` toggles exactly once. Without this a
 *     popover closes and reopens in the same gesture and looks like it never
 *     closes at all.
 *   - *Closing mid-drag.* Dismissal keys off `pointerdown`, not `pointerup` or
 *     `click`. Dragging the saturation square and releasing past the panel edge
 *     would otherwise shut it in the middle of choosing a colour.
 *   - *Swallowing the click that dismisses it.* No overlay, no `preventDefault`.
 *     Clicking a button elsewhere closes this popover and presses that button,
 *     which is what people expect and what a scrim would break.
 *   - *Trapping focus.* Opening does not steal it. Escape closes and returns it
 *     to the anchor, which is the one case where moving focus is right.
 */

export interface PopoverOptions {
  /** The control that opens it. Gets `aria-expanded` and `aria-haspopup`. */
  anchor: HTMLElement;
  /** The floating element. Gets `data-open` when visible. */
  panel: HTMLElement;
  /**
   * Position the panel. Called on every open, *after* `data-open` is set.
   *
   * That order is required rather than incidental: a `display: none` element
   * has no box, so measuring it to decide where it fits returns zeros. Both
   * happen in the same task, so the browser paints once and there is no flash
   * at the old position.
   */
  place?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  /**
   * A second element that also counts as inside, for dismissal.
   *
   * For a popover whose real trigger is not its `anchor`. The tool options
   * panel hangs from a detached node so that attachPopover's own click-toggle
   * cannot fight the tool button's selection handler — but that left the button
   * counting as *outside*, so pressing it fired this dismissal on pointerdown
   * and the button's own handler reopened the panel on click. It could never be
   * closed by pressing the thing that opened it.
   *
   * A getter, because which element it is changes: the panel opens from
   * whichever tool is active.
   */
  alsoInside?: () => HTMLElement | null;
}

/**
 * One popover per anchor, enforced.
 *
 * Attaching twice binds two click handlers to the same button, so a single
 * click toggles twice and the panel appears never to open — the same visible
 * symptom as the specificity bug this module was written to fix, from a
 * completely different cause. Dev hot-reload re-runs module setup and hits this
 * immediately; a second `createColourPicker` call in real code would too.
 */
const attached = new WeakMap<HTMLElement, { destroy(): void }>();

export function attachPopover(opts: PopoverOptions) {
  const { anchor, panel } = opts;
  attached.get(anchor)?.destroy();
  let open = false;

  /**
   * True while a pointer that went down inside is still down.
   *
   * A drag from the saturation square to somewhere past the panel edge ends
   * with a `pointerup` outside — harmless, because dismissal watches
   * `pointerdown`. This flag covers the rarer inverse: a synthetic or
   * retargeted `pointerdown` arriving outside while an interaction is in
   * flight.
   */
  let pointerInside = false;

  const onDocPointerDown = (e: Event) => {
    if (!open || pointerInside) return;
    const target = e.target as Node | null;
    if (!target) return;
    // Composed path, so a click inside a shadow root still counts as inside.
    if (panel.contains(target) || anchor.contains(target)) return;
    if (opts.alsoInside?.()?.contains(target)) return;
    api.close();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!open || e.key !== "Escape") return;
    api.close();
    // Escape is the one dismissal where focus should come back — the user is on
    // the keyboard by definition, and leaving focus on a hidden panel strands them.
    anchor.focus();
  };

  panel.addEventListener("pointerdown", () => {
    pointerInside = true;
  });
  window.addEventListener("pointerup", () => {
    pointerInside = false;
  });

  document.addEventListener("pointerdown", onDocPointerDown);
  document.addEventListener("keydown", onKeyDown);

  const api = {
    get isOpen() {
      return open;
    },

    open() {
      if (open) return;
      open = true;
      panel.setAttribute("data-open", "");
      opts.place?.(); // after display is on — see PopoverOptions.place
      anchor.setAttribute("aria-expanded", "true");
      opts.onOpen?.();
    },

    close() {
      if (!open) return;
      open = false;
      panel.removeAttribute("data-open");
      anchor.setAttribute("aria-expanded", "false");
      opts.onClose?.();
    },

    toggle() {
      if (open) api.close();
      else api.open();
    },

    destroy() {
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      anchor.removeEventListener("click", onAnchorClick);
      if (attached.get(anchor) === api) attached.delete(anchor);
      api.close();
    },
  };

  // The anchor toggles on `click`, not `pointerdown`, so keyboard activation
  // works for free — Enter and Space both synthesise a click.
  const onAnchorClick = () => api.toggle();
  anchor.addEventListener("click", onAnchorClick);
  anchor.setAttribute("aria-haspopup", "dialog");
  anchor.setAttribute("aria-expanded", "false");

  attached.set(anchor, api);
  return api;
}

export type Popover = ReturnType<typeof attachPopover>;
